import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { FileSignature, Key, Video, GraduationCap, Rocket, CheckCircle, CreditCard } from 'lucide-react';
interface NextStepsSlideProps {
  isDark?: boolean;
}
export function NextStepsSlide({
  isDark = false
}: NextStepsSlideProps) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };
  const steps = [{
    icon: FileSignature,
    title: 'Sign & Confirm',
    description: 'Pilot Agreement sent via DocuSign. Once signed, pilot is officially confirmed.',
    color: 'from-heliona-navy to-heliona-slate'
  }, {
    icon: CreditCard,
    title: 'Payment',
    description: 'Invoice sent and payment processed to finalize the engagement.',
    color: 'from-amber-600 to-amber-700'
  }, {
    icon: Key,
    title: 'Secure Access',
    description: "After payment confirmation, admin receives access to Heliona's onboarding portal to begin setup.",
    color: 'from-heliona-slate to-heliona-navy'
  }, {
    icon: Video,
    title: 'Kickoff Call',
    description: 'Align on goals, confirm pilot cohort, and review the 90-day timeline.',
    color: 'from-heliona-orange to-amber-600'
  }, {
    icon: CheckCircle,
    title: 'Admin Orientation',
    description: 'Orientation email with onboarding confirmation and training video access.',
    color: 'from-heliona-mint to-heliona-mint-dark'
  }, {
    icon: GraduationCap,
    title: 'Training & Launch',
    description: 'Training on how Heliona works, the 4C Framework, and platform engagement.',
    color: 'from-heliona-mint-dark to-heliona-mint'
  }, {
    icon: Rocket,
    title: 'Pilot Begins',
    description: 'Managers complete Culture Check, receive Helium Score, and start using Heliona.',
    color: 'from-heliona-navy to-heliona-mint'
  }];
  return <motion.div className="h-full w-full overflow-y-auto overflow-x-hidden" variants={containerVariants} initial="hidden" animate="visible" data-id="element-444">
      <div className="min-h-full flex flex-col justify-start md:justify-center max-w-5xl mx-auto px-4 sm:px-8 py-16 sm:py-20" data-id="element-445">
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-8 sm:mb-12" data-id="element-446">
          <h1 className={`text-2xl sm:text-4xl font-serif font-medium mb-3 sm:mb-4 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-447">
            What Happens Next?
          </h1>
          <p className={`text-sm sm:text-lg max-w-2xl mx-auto leading-relaxed px-4 ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-448">
            From agreement to results in 90 days.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="space-y-4 sm:space-y-5 mb-8 sm:mb-12" data-id="element-449">
          {steps.map((step, index) => {
          const Icon = step.icon;
          return <motion.div key={index} variants={itemVariants} className={`relative ${index < steps.length - 1 ? 'pb-4 sm:pb-5' : ''}`} data-id="element-450">
                <div className={`rounded-xl p-4 sm:p-6 transition-all hover:shadow-md ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-white border border-heliona-beige'}`} data-id="element-451">
                  <div className="flex items-start gap-3 sm:gap-4" data-id="element-452">
                    {/* Icon */}
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center flex-shrink-0 shadow-sm`} data-id="element-453">
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" data-id="element-454" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0" data-id="element-455">
                      <h3 className={`text-base sm:text-lg font-bold mb-2 leading-tight ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-456">
                        {step.title}
                      </h3>
                      <p className={`text-xs sm:text-sm leading-relaxed ${isDark ? 'text-white/70' : 'text-heliona-text-gray'}`} data-id="element-457">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Connector Line */}
                {index < steps.length - 1 && <div className={`absolute left-[1.75rem] sm:left-[2.25rem] top-[4.5rem] sm:top-[5.5rem] w-0.5 h-4 sm:h-5 bg-gradient-to-b to-transparent ${isDark ? 'from-white/10' : 'from-heliona-beige'}`} data-id="element-458" />}
              </motion.div>;
        })}
        </div>

        {/* Footer */}
        <motion.div variants={itemVariants} className="text-center mb-16 sm:mb-0" data-id="element-459">
          <p className={`text-xs sm:text-sm italic leading-relaxed px-4 ${isDark ? 'text-white/60' : 'text-heliona-text-gray/70'}`} data-id="element-460">
            A structured onboarding process designed for clarity and momentum.
          </p>
        </motion.div>
      </div>
    </motion.div>;
}